import { LitElement, html, css } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map.js';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';
import { actions, selectors } from './';
import { selectors as userSelectors } from '../user';
import { connect } from '../store/connect';
import { getRouter } from '../routing/route-base';
import { SELECT_MODES } from '../table/table-constants';
import { DETAIL_MODES } from '../form/constants';

import '@trumedianetworks/tmn-core-components/src/tmn-grid';
import '@trumedianetworks/tmn-core-components/src/tmn-select';
import '@trumedianetworks/tmn-core-components/src/tmn-form-group';
import '@trumedianetworks/tmn-core-components/src/tmn-input';
import '@trumedianetworks/tmn-core-components/src/tmn-loading';
import '@trumedianetworks/tmn-core-components/src/date-picker/tmn-date-range-picker';
import '../table/tmn-table-editable';
import './tmn-packages-cell-formatter';
import './retry-package-btn';
import './package-detail';

const PACKAGE_STATE_OPTIONS = [
    {
        name: 'initialized',
        displayKey: 'initialized',
    },
    {
        name: 'processing',
        displayKey: 'processing',
    },
    {
        name: 'ready',
        displayKey: 'ready',
    },
    {
        name: 'soft_failed',
        displayKey: 'soft failed',
    },
    {
        name: 'failed',
        displayKey: 'failed',
    },
];

const dateFormatFn = (value, row) => {
    const jsDate = new Date(parseInt(value, 10));
    return value
        ? html`${dayjs(jsDate).format('YYYY-MM-DD h:MM A')}`
        : html`<div style="text-align:center">-</div>`;
};

class PackageList extends LitElement {
    constructor() {
        super();
        this.fetchActionDebounced = debounce(this._fetchAction, 350);

        // could move _columnDefs to updated if we want to reflect isMobile changes
        const { isMobile } = this;
        this.packages = [];
        this._columnDefs = [
            {
                label: 'Id',
                key: 'id',
                sortable: true,
                search: true,
            },
            {
                label: 'Email',
                key: 'authorId',
                sortable: true,
                search: true,
            },
            {
                label: 'Name',
                key: 'name',
                hideInDetailMode: isMobile,
                sortable: true,
                search: true,
            },
            {
                label: 'Site',
                key: 'sitename',
                hideInDetailMode: isMobile,
                sortable: true,
                search: true,
            },
            {
                key: 'Type',
                label: 'type',
                hideInDetailMode: true,
                sortable: true,
                search: true,
                dataFormatter: (value, row) => {
                    const { importerType, exporterType } = row.metaData.rawData;

                    if (importerType === 'videoConcatenate') {
                        return 'MP4';
                    } else if (exporterType === 'zip') {
                        return 'Offline Playlist';
                    } else if (exporterType === 'googleDrive') {
                        return 'Google Drive';
                    } else {
                        return 'other';
                    }
                },
            },
            {
                label: 'Created',
                key: 'created',
                hideInDetailMode: true,
                sortable: true,
                search: true,
                dataFormatter: dateFormatFn,
            },
            {
                label: 'Updated',
                key: 'updated',
                hideInDetailMode: true,
                sortable: true,
                search: true,
                dataFormatter: dateFormatFn,
            },
            {
                label: 'State',
                key: 'state',
                hideInDetailMode: true,
                sortable: true,
                search: true,
            },
            {
                label: 'Retry Count',
                key: 'retryCount',
                hideInDetailMode: true,
                sortable: true,
            },
            {
                label: 'Error',
                key: 'errorMessage',
                hideInDetailMode: true,
                sortable: true,
                classes: {
                    'error-message': true,
                },
                dataFormatter: (value, row) => {
                    return html`<tmn-packages-cell-formatter
                        .value=${value}
                        .row=${row}
                        .format=${'error-message'}></tmn-packages-cell-formatter>`;
                },
            },
            {
                key: '_',
                label: '',
                hideInDetailMode: true,
                dataFormatter: (value, row) => {
                    return this.renderActions(row);
                },
            },
        ];
    }

    static get styles() {
        return [
            css`
                :host {
                    --table-cell-height: 28px;
                }
                .package-filters {
                    margin-top: 10px;
                    margin-bottom: 10px;
                }

                tmn-form-group {
                    margin-right: 5px;
                }

                .refresh-btn {
                    height: 32px;
                    display: flex;
                    align-self: center;
                }
                .description {
                    font-weight: 600;
                }
                /* .table-container {
                } */
            `,
        ];
    }

    static get properties() {
        return {
            _columnDefs: {
                type: Array,
            },
            filters: {
                type: Object,
            },
            allPackages: {
                type: Array,
            },
            sitename: {
                type: String,
            },
            isLoading: {
                type: Boolean,
            },
            selectedPackageIds: {
                type: Array,
            },
            isMobile: {
                type: Boolean,
            },
        };
    }

    updated(changedProps) {
        if (changedProps.has('filters')) {
            this.fetchActionDebounced();
        }
    }

    _fetchAction() {
        this.actions.fetchPackageList(this.sitename, this.filters);
    }

    renderFilters() {
        const selections = PACKAGE_STATE_OPTIONS.filter((sd) =>
            this.filters.state.includes(sd.name)
        );
        const { date } = this.filters;
        const from = date?.length && date[0];
        const to = date?.length && date[1];
        return html`<tmn-grid container direction="row" class="package-filters" slot="left-actions">
            <tmn-form-group
                ><label>User</label>
                <tmn-input
                    @tmn-input-changed=${this.handleEmailChanged}
                    placeholder="Email Address"
                    type="text"
                    .value=${this.filters.email || ''}></tmn-input
            ></tmn-form-group>
            <tmn-form-group>
                <label>State</label>
                <tmn-select
                    @tmn-selections-changed=${this.handleSelectedStateChanged}
                    multiple
                    .data=${PACKAGE_STATE_OPTIONS}
                    .selections=${selections}
                    ><tmn-select></tmn-select
                ></tmn-select>
            </tmn-form-group>
            <tmn-form-group
                ><label>Date</label>
                <tmn-date-range-picker
                    .from=${from}
                    .to=${to}
                    @tmn-date-range-changed=${this._setDateRange}
                    @tmn-tool-closed=${this.handleDateChanged}></tmn-date-range-picker
            ></tmn-form-group>
            <tmn-button class="refresh-btn" @click=${this.handleRefreshClick}>Refresh</tmn-button>
        </tmn-grid>`;
    }

    render() {
        const { allPackages, _columnDefs, isLoading, selectedPackageIds } = this;
        if (isLoading) {
            return html`<tmn-loading class="loading" size="40px" fontSize="20px"> </tmn-loading>`;
        }
        return html`
            <div class="table-container">
                <tmn-table-editable
                    .canAddNew=${false}
                    @tmn-row-select-toggle=${this.handleRowSelectToggle}
                    @tmn-table-editable-close-details=${this.handleCloseDetails}
                    .rowIdentifier=${'id'}
                    .dataRows=${allPackages}
                    .selectedRowIds=${selectedPackageIds}
                    .columnDefs=${_columnDefs}
                    .detailMode=${DETAIL_MODES.VIEW}
                    .selectMode=${SELECT_MODES.SINGLE}
                    .useSearch=${false}
                    .useFilters=${false}>
                    ${this.renderFilters()} ${this.renderDetail()}
                </tmn-table-editable>
            </div>
        `;
    }

    renderDetail() {
        return html`<package-detail slot="detail"></package-detail>`;
    }

    handleCloseDetails(e) {
        const router = getRouter();
        router.navigateByName('video-service-list', {});
    }

    handleRowSelectToggle(e) {
        const { selected, id } = e.detail;
        const router = getRouter();
        if (selected) {
            router.navigateByName('video-service-detail', { packageId: id });
        } else {
            router.navigateByName('video-service-list', {});
        }
    }

    // needed to avoid triggering the row click
    stopPropagation(e) {
        if (e) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
        }
    }

    renderActions(row) {}

    handleEmailChanged(e) {
        const email = e.detail.value;

        this.actions.setFilter({ email });
    }

    handleSelectedStateChanged(e) {
        const selectedState = e.detail.selections;

        this.actions.setFilter({ state: selectedState.map((s) => s.name) });
    }

    _setDateRange(e) {
        const { from, to, rangeSelections, updateType } = e.detail;
        this.startDate = from;
        this.endDate = to;
        this.rangeSelections = rangeSelections;

        if (updateType === 'select') {
            this.handleDateChanged();
        }
    }

    handleDateChanged() {
        this.actions.setFilter({ date: [this.startDate || null, this.endDate || null] });
    }

    handleRefreshClick(e) {
        this.actions.fetchPackageList(this.sitename, this.filters);
    }
}

const mapStateToProps = (state, ownProps) => {
    return (state) => {
        return {
            allPackages: selectors.packageListSelector(state),
            filters: selectors.filtersSelector(state),
            sitename: userSelectors.activeAuthSiteNameSelector(state),
            isLoading: selectors.packageListIsLoadingSelector(state),
            selectedPackageIds: selectors.selectedPackageIdsSelector(state),
        };
    };
};

const combinedActions = {
    ...actions,
};
customElements.define('package-list', connect(mapStateToProps, combinedActions)(PackageList));
